import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Occupancy,
  Override,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DAY_OVERRIDE_MODAL_TAB_IDS,
  type DayOverrideModalTabIds,
  MIN_LOS_DEFAULT_VALUE,
  MIN_LOS_NO_RESTRICTION_VALUE,
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  OVERRIDE_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  MinimalRestrictionAction,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './day-override-modal.module.css';
import OverbookingPanel from './overbooking-panel';
import Sidebar, { type SidebarItem } from './sidebar';
import StayRestrictionsPanel from './stay-restrictions-panel';

interface HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature {
  Args: {
    onCloseModal: () => void;
    period: Period;
  };
  Blocks: {
    default: [];
  };
}

interface SendRestrictionConfig {
  existingOverride: MinLosOverride | OverbookingOverride | null | undefined;
  getRestrictionValues: () => RestrictionActionValue[] | null;
  overrideRemoved: boolean;
  overrideType: RestrictionOverridePayload['type'];
  overrideValue: number | null;
}

export default class DayOverrideModal extends Component<HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTabId: DayOverrideModalTabIds =
    DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
  @tracked minLos: number | null = null;
  @tracked minLosAutomationOn: boolean = false;
  @tracked minLosRestrictionType: MinLosRestrictionType =
    MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
  @tracked minLosTouched = false;
  @tracked overbooking: number | null = null;
  @tracked overbookingAutomationOn: boolean = false;
  @tracked overbookingTouched = false;

  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking minLosOverrideRemoved mid-send. */
  initiallyHadMinLosOverride: boolean = false;
  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking overbookingOverrideRemoved mid-send. */
  initiallyHadOverbookingOverride: boolean = false;

  get hasRestrictionsTabToggle(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.DAY_OVERRIDE_MODAL_RESTRICTIONS_TAB,
    );
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.period.hotelLevelRestriction;
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.hotelLevelRestrictionRecommendation;
  }

  get isOverbooked(): boolean {
    return (
      typeof this.physicalRoomsAvailable === 'number' &&
      this.physicalRoomsAvailable < 0
    );
  }

  get isSending(): boolean {
    const isActionInProgress =
      this.activeTabId === DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS
        ? this.args.period.hasMinLosActionInProgress
        : this.args.period.hasOverbookingActionInProgress;
    const isSending =
      isActionInProgress ||
      this.restrictionsDataRequestService.createRestrictionActionTask.isRunning;
    const isDeletingOverride =
      this.pricingAssistantDataRequestService.deleteOverrideTask.isRunning;
    const isReRequestingData =
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning;

    return isSending || isDeletingOverride || isReRequestingData;
  }

  get minLosErrorMessage(): string | null {
    if (!this.minLosTouched || this.minLosAutomationOn) {
      return null;
    }
    if (this.minLosRestrictionType !== MIN_LOS_RESTRICTION_TYPE.MIN_LOS) {
      return null;
    }
    if (this.minLos === null) {
      return this.intl.t('general.required');
    }
    if (this.minLos <= 1) {
      return this.intl.t('pricing_manager.restrictions.min_los_min_error');
    }
    return null;
  }

  get minLosOverrideRemoved(): boolean {
    return this.minLosAutomationOn && this.initiallyHadMinLosOverride;
  }

  get modalTitle(): string {
    const carate = this.args.period.carates?.[0];
    const day = carate?.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    return `${this.intl.t('general.edit')} ${day}, ${this.intl.t('pricing_assistant.all_rooms')}`;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  /* True when the user has toggled automation ON from an initial OFF state,
     meaning the existing override will be deleted on Send. */
  get overbookingOverrideRemoved(): boolean {
    return this.overbookingAutomationOn && this.initiallyHadOverbookingOverride;
  }

  get physicalRoomsAvailable(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking.physical_rooms_available_tooltip',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking.physical_rooms_available_tooltip',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get roomsForSaleValue(): string | number {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking ?? 0, // Treat empty overbooking (null) as 0 for calculation purposes
      );
    }

    return this.intl.t('general.missing');
  }

  get showOverbookingException(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverbookingOverride(): boolean {
    return (
      this.args.period.hasOverbookingOverride && !this.showOverbookingException
    );
  }

  get showOverbookingTab(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_OVERBOOKING_AUTOPILOT,
      ) && this.access.hasPricingAssistantOverbookingRestrictionsFeature
    );
  }

  get showStayRestrictionsTab(): boolean {
    return (
      this.hasRestrictionsTabToggle &&
      this.access.hasPricingAssistantMinLosRestrictionsFeature
    );
  }

  get sidebarItems(): SidebarItem[] {
    const items: SidebarItem[] = [];

    if (this.showOverbookingTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING,
        hasException: this.showOverbookingException,
        hasOverride: this.showOverbookingOverride,
        label: this.intl.t('pricing_manager.overbooking.sidebar_label'),
      });
    }

    if (this.showStayRestrictionsTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS,
        hasOverride: this.args.period.hasMinLosOverride,
        label: this.intl.t('pricing_manager.restrictions.sidebar_label'),
      });
    }

    return items;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature['Args'],
  ) {
    super(owner, args);
    this.overbooking = this.args.period.effectiveOverbookingWithOverride;
    // Workaround: BE still sends the failed override value when a push fails, showing an exception instead.
    // Treat the override as absent so overbookingOverrideRemoved stays correct if the exception clears before send.
    // Remove once COA-3687 is resolved (tracked in COA-3693).
    if (this.showOverbookingException) {
      this.initiallyHadOverbookingOverride = false;
    } else {
      this.initiallyHadOverbookingOverride =
        this.args.period.hasOverbookingOverride;
    }
    this.overbookingAutomationOn = !this.initiallyHadOverbookingOverride;
    this.initiallyHadMinLosOverride = this.args.period.hasMinLosOverride;
    this.minLosAutomationOn = !this.initiallyHadMinLosOverride;
    this.resetMinLosState();
  }

  private getMinLosRestrictionValues(): RestrictionActionValue[] | null {
    if (this.minLosOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.minLosValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.MIN_LOS, value: recommendationValue }]
        : null;
    }

    if (
      this.minLosRestrictionType === MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
    ) {
      return [
        {
          type: RESTRICTION_TYPES.MIN_LOS,
          value: MIN_LOS_NO_RESTRICTION_VALUE,
        },
      ];
    }

    if (this.minLos == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.MIN_LOS, value: this.minLos }];
  }

  private getOverbookingRestrictionValues(): RestrictionActionValue[] | null {
    if (this.overbookingOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.overbookingValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.OVERBOOKING, value: recommendationValue }]
        : null;
    }

    if (this.overbooking == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.OVERBOOKING, value: this.overbooking }];
  }

  private resetMinLosState(): void {
    if (this.minLosAutomationOn) {
      return;
    }

    const overrideValue = this.hotelLevelRestriction?.minLosOverride?.value;

    if (overrideValue === MIN_LOS_NO_RESTRICTION_VALUE) {
      this.minLos = MIN_LOS_NO_RESTRICTION_VALUE;
      this.minLosRestrictionType = MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION;
    } else {
      this.minLos = overrideValue ?? MIN_LOS_DEFAULT_VALUE;
      this.minLosRestrictionType = MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
    }
  }

  private async sendMinLos(): Promise<'success' | 'error' | null> {
    const overrideValue =
      this.minLosRestrictionType === MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
        ? MIN_LOS_NO_RESTRICTION_VALUE
        : this.minLos;

    return this.sendRestrictionAction({
      existingOverride: this.hotelLevelRestriction?.minLosOverride,
      overrideRemoved: this.minLosOverrideRemoved,
      overrideValue,
      overrideType: OVERRIDE_TYPES.MIN_LOS,
      getRestrictionValues: () => this.getMinLosRestrictionValues(),
    });
  }

  private async sendOverbooking(): Promise<'success' | 'error' | null> {
    return this.sendRestrictionAction({
      existingOverride: this.hotelLevelRestriction?.overbookingOverride,
      overrideRemoved: this.overbookingOverrideRemoved,
      overrideValue: this.overbooking,
      overrideType: OVERRIDE_TYPES.OVERBOOKING,
      getRestrictionValues: () => this.getOverbookingRestrictionValues(),
    });
  }

  private async sendRestrictionAction(
    config: SendRestrictionConfig,
  ): Promise<'success' | 'error' | null> {
    const {
      existingOverride,
      overrideRemoved,
      overrideValue,
      overrideType,
      getRestrictionValues,
    } = config;
    const formattedDate = this.args.period.fromDate.format('YYYY-MM-DD');
    const hotelId = this.hotelAndMarketService.hotel.id!;

    if (overrideRemoved && existingOverride) {
      const result =
        await this.pricingAssistantDataRequestService.deleteOverrideTask.perform(
          existingOverride.id,
        );

      if (!result?.isSuccess) {
        return 'error';
      }

      // Pre-unload so stale records don't persist if absent from the re-request response
      [...this.args.period.restrictions].forEach((r) =>
        this.store.unloadRecord(r),
      );
      [...this.args.period.restrictionRecommendations].forEach((r) =>
        this.store.unloadRecord(r),
      );

      await Promise.all([
        this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
        this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
      ]);
    }

    const shouldSendOverride = !overrideRemoved;
    const override: RestrictionOverridePayload | undefined =
      shouldSendOverride && overrideValue !== null
        ? {
            subscription_id: parseInt(hotelId, 10),
            from_date: formattedDate,
            value: overrideValue,
            type: overrideType,
            context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
          }
        : undefined;

    // Called by the poller after the push completes to refresh override data
    this.restrictionsDataRequestService.overrideCallbackOnPushFinished = async (
      day: string,
      subscriptionId: string,
    ): Promise<void> => {
      this.store
        .peekAll<Override>('override')
        .filter((o: Override) => o.fromDate.format('YYYY-MM-DD') === day)
        .forEach((o: Override) => this.store.unloadRecord(o));

      await this.pricingAssistantDataRequestService.requestOverridesTask.perform(
        subscriptionId,
        day,
        day,
      );
    };

    // getRestrictionValues is called after the potential re-request so it reads fresh recommendation data
    const restrictionValues = getRestrictionValues();
    const restrictionData: MinimalRestrictionAction | undefined =
      restrictionValues !== null
        ? { from_date: formattedDate, values: restrictionValues }
        : undefined;

    if (!restrictionData) {
      return overrideRemoved ? 'success' : null;
    }

    try {
      await this.restrictionsDataRequestService.sendRestriction(
        restrictionData,
        RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        override,
      );

      return 'success';
    } catch {
      return 'error';
    }
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  onMinLosBlur(): void {
    this.minLosTouched = true;
  }

  @action
  onMinLosTypeChange(event: Event): void {
    const type = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
    /* minLos is intentionally not updated — the number input value is preserved
       so switching back to MIN_LOS restores whatever the user had typed before. */
    this.minLosRestrictionType = type;
  }

  @action
  onOverbookingBlur(): void {
    this.overbookingTouched = true;
  }

  @action
  async onSend(): Promise<void> {
    if (this.isSending) {
      return;
    }

    const isMinLos =
      this.activeTabId === DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS;
    const isOverbooking =
      this.activeTabId === DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
    const automationOn = isMinLos
      ? this.minLosAutomationOn
      : this.overbookingAutomationOn;
    const overrideRemoved = isMinLos
      ? this.minLosOverrideRemoved
      : this.overbookingOverrideRemoved;

    if (isMinLos) {
      this.minLosTouched = true;
    } else {
      this.overbookingTouched = true;
    }
    if (isMinLos && this.minLosErrorMessage !== null) {
      return;
    }
    /* Block send when overbooking value is empty */
    if (isOverbooking && !automationOn && this.overbooking === null) {
      return;
    }

    // Automation is on and there's no override to remove — nothing to do
    if (automationOn && !overrideRemoved) {
      this.onCloseModal();
      return;
    }

    this.usageAnalytics.track(
      overrideRemoved
        ? 'Autopilot Override Disabled'
        : 'Autopilot Override Enabled',
      {
        eventTrigger: 'day-override-modal',
        product: 'pricing-manager',
        overrideType: isMinLos ? 'min_los' : 'overbooking',
      },
    );

    const outcome = isMinLos
      ? await this.sendMinLos()
      : await this.sendOverbooking();

    if (outcome === 'success') {
      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } else if (outcome === 'error') {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }

    this.onCloseModal();
  }

  @action
  selectTab(id: DayOverrideModalTabIds): void {
    this.activeTabId = id;
  }

  @action
  toggleMinLosAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.minLosAutomationOn = checked;
    this.resetMinLosState();

    if (!checked) {
      this.minLosTouched = false;
    }
  }

  @action
  toggleOverbookingAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.overbookingAutomationOn = checked;

    if (checked && this.initiallyHadOverbookingOverride) {
      // Turning automation ON when override exists — show recommendation value
      this.overbooking = this.args.period.effectiveOverbooking;
    } else if (!checked) {
      // Turning automation OFF — restore the value from when the modal opened
      this.overbooking = this.args.period.effectiveOverbookingWithOverride;
      this.overbookingTouched = false;
    }
  }

  @action
  updateMinLos(value: number | null): void {
    this.minLos = value;
  }

  @action
  updateOverbooking(value: number | null): void {
    this.overbooking = value;
  }

  <template>
    <PrismModal
      @onClose={{this.onCloseModal}}
      @title={{this.modalTitle}}
      class={{if this.hasRestrictionsTabToggle styles.modal}}
    >
      <:default>
        {{#if this.hasRestrictionsTabToggle}}
          <div class={{styles.body}}>
            <div class="text-300--regular">
              <Sidebar
                @activeTabId={{this.activeTabId}}
                @items={{this.sidebarItems}}
                @onSelect={{this.selectTab}}
              />
            </div>
            <div>
              {{#if
                (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING)
              }}
                <OverbookingPanel
                  @automationOn={{this.overbookingAutomationOn}}
                  @effectiveOverbooking={{@period.effectiveOverbooking}}
                  @effectiveOverbookingWithOverride={{@period.effectiveOverbookingWithOverride}}
                  @failedOverbookingValue={{@period.failedOverbookingValue}}
                  @hasOverbookingOverride={{this.showOverbookingOverride}}
                  @isOverbooked={{this.isOverbooked}}
                  @isSending={{this.isSending}}
                  @isTouched={{this.overbookingTouched}}
                  @onOverbookingBlur={{this.onOverbookingBlur}}
                  @onToggleAutomation={{this.toggleOverbookingAutomation}}
                  @onUpdateOverbooking={{this.updateOverbooking}}
                  @overbooking={{this.overbooking}}
                  @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
                  @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
                  @roomsForSaleValue={{this.roomsForSaleValue}}
                  @showOverbookingException={{this.showOverbookingException}}
                />
              {{else if
                (eq
                  this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS
                )
              }}
                <StayRestrictionsPanel
                  @automationOn={{this.minLosAutomationOn}}
                  @effectiveMinLos={{@period.effectiveMinLos}}
                  @errorMessage={{this.minLosErrorMessage}}
                  @hasMinLosOverride={{@period.hasMinLosOverride}}
                  @isTouched={{this.minLosTouched}}
                  @minLos={{this.minLos}}
                  @minLosRestrictionType={{this.minLosRestrictionType}}
                  @onMinLosBlur={{this.onMinLosBlur}}
                  @onMinLosTypeChange={{this.onMinLosTypeChange}}
                  @onToggleAutomation={{this.toggleMinLosAutomation}}
                  @onUpdateMinLos={{this.updateMinLos}}
                />
              {{/if}}
            </div>
          </div>
        {{else}}
          <OverbookingPanel
            @automationOn={{this.overbookingAutomationOn}}
            @effectiveOverbooking={{@period.effectiveOverbooking}}
            @effectiveOverbookingWithOverride={{@period.effectiveOverbookingWithOverride}}
            @failedOverbookingValue={{@period.failedOverbookingValue}}
            @hasOverbookingOverride={{this.showOverbookingOverride}}
            @isOverbooked={{this.isOverbooked}}
            @isSending={{this.isSending}}
            @isTouched={{this.overbookingTouched}}
            @onOverbookingBlur={{this.onOverbookingBlur}}
            @onToggleAutomation={{this.toggleOverbookingAutomation}}
            @onUpdateOverbooking={{this.updateOverbooking}}
            @overbooking={{this.overbooking}}
            @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
            @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
            @roomsForSaleValue={{this.roomsForSaleValue}}
            @showOverbookingException={{this.showOverbookingException}}
          />
        {{/if}}
      </:default>
      <:footer>
        <div
          class="mx-500 flex h-full flex-row items-center justify-end gap-200"
        >
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onCloseModal}}
          />
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isLoading={{this.isSending}}
            @label={{t "pricing_assistant.label.push"}}
            @onClick={{this.onSend}}
            data-test-id="day-override-send-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
